import { useEffect, useState } from "react";
import { Copy, Loader2, PlugZap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";

const Row = ({ l, v, testid }) => <div className="flex justify-between gap-4 py-1 border-b border-slate-100"><span className="text-slate-500">{l}</span><span className="font-medium text-right" data-testid={testid}>{v}</span></div>;

const SRC = { env: "Ortam değişkeni (SHIPSGO_API_KEY / Secrets)", db: "ERP'de kayıtlı anahtar (şifreli)" };

function KeyPanel({ s, onDone, setTest }) {
  const { user } = useAuth();
  const [k, setK] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const info = s.key || {};
  const go = async (del) => {
    if (!pw) { toast.error("Parolanızı girin"); return; }
    if (!del && !k.trim()) { toast.error("API anahtarını girin"); return; }
    setBusy(true);
    try {
      const { data } = del ? await api.delete("/integrations/shipsgo/key", { data: { password: pw } }) : await api.put("/integrations/shipsgo/key", { key: k, password: pw });
      setK(""); setPw("");
      if (del) toast.success("ERP'deki anahtar silindi"); else { setTest(data.test); data.test.ok ? toast.success(`Anahtar kaydedildi · ${data.test.message}`) : toast.error(`Anahtar kaydedildi · ${data.test.message}`); }
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 rounded border border-slate-200 p-3 text-[12px] space-y-2" data-testid="shipsgo-key-panel">
      <div className="flex justify-between"><span className="text-slate-500">Etkin kaynak</span><b data-testid="shipsgo-key-source">{SRC[info.source] || "Tanımlı değil"}</b></div>
      <div className="flex justify-between"><span className="text-slate-500">ERP anahtarı</span>
        <span data-testid="shipsgo-key-masked">{info.db_present ? `Tanımlı (son 4 karakter: ${info.db_masked}) · ${formatDate(info.db_saved_at)} · ${info.db_saved_by_name}` : "Yok"}</span></div>
      {info.db_error && <div className="text-rose-700" data-testid="shipsgo-key-db-error">{info.db_error}</div>}
      {info.env_present && <div className="text-amber-700">Ortam değişkeni dolu olduğu için ERP anahtarı kullanılmaz.</div>}
      {user?.is_super ? <>
        <Input type="password" autoComplete="new-password" placeholder="API Anahtarı" value={k} onChange={(e) => setK(e.target.value)} data-testid="shipsgo-key-input" />
        <Input type="password" autoComplete="current-password" placeholder="Parolanız (doğrulama)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="shipsgo-key-password" />
        <div className="flex gap-2">
          <Button size="sm" disabled={busy} onClick={() => go(false)} data-testid="shipsgo-key-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Kaydet</Button>
          {info.db_present && <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" disabled={busy} onClick={() => go(true)} data-testid="shipsgo-key-delete">Sil</Button>}
        </div>
        <div className="text-[11px] text-slate-400">Anahtar sunucuda AES-256-GCM ile şifreli saklanır, hiçbir ekranda/yanıtta gösterilmez. Kayıttan sonra bağlantı testi (yalnız GET, kredi harcamaz) otomatik çalışır.</div>
      </> : <div className="text-slate-400">Anahtarı yalnız Süper Yönetici girebilir.</div>}
    </div>
  );
}

function WebhookBox({ s, onDone }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [rot, setRot] = useState(false);
  const [pw, setPw] = useState("");
  const copy = async () => {
    try { const { data } = await api.get("/integrations/shipsgo/webhook-url", { headers: { "Cache-Control": "no-store" } }); await navigator.clipboard.writeText(data.url); setCopied(true); toast.success("Kopyalandı"); setTimeout(() => setCopied(false), 2500); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rotate = async () => {
    try { const { data } = await api.post("/integrations/shipsgo/webhook-token/rotate", { password: pw }); toast.warning(data.warning); setRot(false); setPw(""); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (<div className="space-y-2">
    <div className="flex gap-2 items-center"><code className="text-[11px] bg-slate-100 rounded px-2 py-1 break-all" data-testid="shipsgo-webhook-url">{s.webhook_url_masked}</code>
      {user?.is_super && <Button size="sm" variant="outline" onClick={copy} data-testid="shipsgo-webhook-copy"><Copy className="h-4 w-4 mr-1" />{copied ? "Kopyalandı" : "Kopyala"}</Button>}
      {user?.is_super && <Button size="sm" variant="outline" onClick={() => setRot(!rot)} data-testid="shipsgo-webhook-rotate-toggle">Yenile</Button>}</div>
    <div className="text-[11px] text-slate-400" data-testid="shipsgo-webhook-source">Kaynak: {s.webhook_source === "env" ? "Ortam değişkeni (SHIPSGO_WEBHOOK_TOKEN) – öncelikli" : "ERP (şifreli)"}</div>
    {rot && <div className="rounded border border-amber-300 bg-amber-50 p-2 space-y-2" data-testid="shipsgo-webhook-rotate-panel">
      <div className="text-amber-800">Token yenilenirse eski URL çalışmaz; yeni URL'yi ShipsGo panelindeki webhook ayarına girmeniz gerekir.</div>
      <Input type="password" placeholder="Parolanız" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="shipsgo-webhook-rotate-password" />
      <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={!pw} onClick={rotate} data-testid="shipsgo-webhook-rotate-confirm">Token'ı Yenile</Button></div>}
  </div>);
}

export default function ShipsGoTab() {
  const [s, setS] = useState(null);
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/integrations/shipsgo/status").then(({ data }) => setS(data)).catch((e) => setS({ error: apiError(e) }));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/integrations/shipsgo/test"); setTest(data); if (data.ok) toast.success(data.message); else toast.error(data.message); load(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (s.error) return <div className="text-rose-600 text-sm" data-testid="shipsgo-error">{s.error}</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="shipsgo-tab">
      <div className="panel p-4 text-[13px]">
        <div className="flex items-center gap-2 mb-2"><PlugZap className="h-5 w-5 text-blue-600" /><b>ShipsGo (API v2)</b>
          <Button size="sm" className="ml-auto bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={run} data-testid="shipsgo-test-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}BAĞLANTIYI TEST ET</Button></div>
        {test && <div className={`mb-2 rounded p-2 text-[12px] ${test.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="shipsgo-test-result">{test.message}</div>}
        <Row l="API anahtarı" v={s.key_present ? "Tanımlı" : "Tanımlı değil"} testid="shipsgo-key-status" />
        <Row l="Bağlantı" v={s.last_http ? (s.last_http < 400 ? `Çalışıyor (HTTP ${s.last_http})` : `Hata (HTTP ${s.last_http})`) : "Henüz denenmedi"} testid="shipsgo-connection" />
        <Row l="Aktif takip" v={s.active_trackings} testid="shipsgo-active" />
        <Row l="Son başarılı senkron" v={s.last_ok_at ? formatDate(s.last_ok_at) : "—"} testid="shipsgo-last-ok" />
        <Row l="Son webhook" v={s.last_webhook_at ? formatDate(s.last_webhook_at) : "—"} testid="shipsgo-last-webhook" />
        <Row l="Kalan kredi" v={s.credits_remaining ?? "API bildirmedi"} testid="shipsgo-credits" />
        <KeyPanel s={s} onDone={load} setTest={setTest} />
        <div className="mt-3 text-[12px]"><div className="text-slate-500 mb-1">Webhook adresi (ShipsGo panelinde Webhooks bölümüne yapıştırın):</div>
          {s.webhook_url_masked ? <WebhookBox s={s} onDone={load} /> : <span className="text-rose-700">Webhook token tanımlı değil</span>}
          <div className="text-[11px] text-slate-400 mt-1">Zamanlanmış yedek: 6 saatte bir yalnız GET ile güncelleme (kredi harcamaz).</div></div>
      </div>
      <div className="panel p-4 text-[12px]" data-testid="shipsgo-errors"><b className="text-[13px]">API hata günlüğü (anahtar maskelenir)</b>
        {s.errors.length === 0 ? <div className="text-slate-400 mt-2">Hata yok</div> : s.errors.map((e, i) => <div key={i} className="border-b border-slate-100 py-1"><span className="font-mono">{formatDate(e.at)}</span> · <b>{e.code}</b>{e.http ? ` (${e.http})` : ""} · {e.path}<div className="text-slate-500">{e.message}</div></div>)}
      </div>
    </div>
  );
}
